import type { PropsWithChildren } from "react";
import { FareRefundabilityV2 as FareRefundability } from "@b2bportal/air-cfar-api";
import {
  IconNameEnum,
  useCfarStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreFlightsCfarOffersComponents } from "@b2bportal/core-types";
import { Icon } from "@b2bportal/core-ui";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { Dialog, DialogTrigger, IframeDialogContent } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./CfarHowItWorksDialog.module.scss";

export interface CfarHowItWorksDialogProps {
  coverageAmount: string;
  termsAndConditionsLink: string;
  cfarFareRefundability?: FareRefundability;
}

export const CfarHowItWorksDialog = ({
  coverageAmount,
  termsAndConditionsLink,
  cfarFareRefundability,
}: CfarHowItWorksDialogProps) => {
  const COMPONENT_KEY = CoreFlightsCfarOffersComponents.CfarHowItWorksDialog;
  const styles = useCfarStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t, brand } = useI18nContext();
  const htsAddOnsLink =
    brand.urls.htsAddOns ?? "https://hts.hopper.com/legal/what-are-hts-add-ons";

  const bookNowText = t("core-flights-cfar.howItWorksDialog.bookNow", {
    defaultValue: "",
  });

  const isFTC =
    cfarFareRefundability === FareRefundability.FullFtc ||
    cfarFareRefundability === FareRefundability.PartialFtc;

  return (
    <div className={block}>
      <div className={cn("header")}>
        <Icon
          iconName={IconNameEnum.cfarHowItWorksLogo}
          className={cn("dialog-icon")}
        />
        <h2 className={cn("dialog-title")}>
          {t("core-flights-cfar.howItWorksDialog.title")}
        </h2>
      </div>
      <div className={cn("body")}>
        {isFTC && (
          <p className={cn("ftc-disclaimer")}>
            <Trans i18nKey="core-flights-cfar.ftcDisclaimer" />
          </p>
        )}
        <div className={cn("postheader-bullets")}>
          <NumBullet
            num={1}
            className={cn("bullet-container")}
            bulletClassName={cn("bullet")}
            textClassName={cn("bullet-text")}
          >
            <Trans
              i18nKey={"core-flights-cfar.howItWorksDialog.bullets.one"}
              components={[<span className={cn("bold-dark")} />]}
            />
          </NumBullet>
          <NumBullet
            num={2}
            className={cn("bullet-container")}
            bulletClassName={cn("bullet")}
            textClassName={cn("bullet-text")}
          >
            <Trans
              i18nKey={"core-flights-cfar.howItWorksDialog.bullets.two"}
              values={{ coverageAmount }}
              components={[<span className={cn("bold-dark")} />]}
            />
          </NumBullet>
        </div>
        <div className={cn("important-information")}>
          <h3 className={cn("important-information-title")}>
            {t("core-flights-cfar.howItWorksDialog.importantInformation.title")}
          </h3>
          <div className={cn("important-information-bullets")}>
            <BulletWithIcon
              icon={{
                iconName: IconNameEnum.importantInformation1,
                className: cn("icon", ["icon1"]),
              }}
              textClassName={cn("bullet-text")}
              className={cn("bullet-container")}
            >
              <Trans
                i18nKey={
                  "core-flights-cfar.howItWorksDialog.importantInformation.one"
                }
                components={[<span className={cn("bold")} />]}
              />
            </BulletWithIcon>
            <BulletWithIcon
              icon={{
                iconName: IconNameEnum.importantInformation2,
                className: cn("icon", ["icon2"]),
              }}
              textClassName={cn("bullet-text")}
              className={cn("bullet-container")}
            >
              <Trans
                i18nKey={
                  "core-flights-cfar.howItWorksDialog.importantInformation.two"
                }
                components={[<span className={cn("bold")} />]}
              />
            </BulletWithIcon>
            <BulletWithIcon
              icon={{
                iconName: IconNameEnum.importantInformation3,
                className: cn("icon", ["icon3"]),
              }}
              textClassName={cn("bullet-text")}
              className={cn("bullet-container")}
            >
              <Trans
                i18nKey={
                  "core-flights-cfar.howItWorksDialog.importantInformation.three"
                }
                components={[<span className={cn("bold")} />]}
              />
            </BulletWithIcon>
            <BulletWithIcon
              icon={{
                iconName: IconNameEnum.importantInformation4,
                className: cn("icon", ["icon4"]),
                iconProps: {
                  useCurrentColor: false,
                },
              }}
              textClassName={cn("bullet-text")}
              className={cn("bullet-container", {
                last: true,
              })}
            >
              <Trans
                i18nKey={
                  "core-flights-cfar.howItWorksDialog.importantInformation.four"
                }
                components={[<span className={cn("bold")} />]}
              />
            </BulletWithIcon>
          </div>
        </div>
        <div className={cn("footer")}>
          {bookNowText && (
            <h3 className={cn("important-information-title")}>{bookNowText}</h3>
          )}
          <span className={cn("footer-text")}>
            <Trans
              i18nKey={
                "core-flights-cfar.howItWorksDialog.footer.cfarDescription"
              }
              components={[
                <Modal
                  className={clsx(cn("hts-addons-link"), cn("link"))}
                  link={htsAddOnsLink}
                  cn={cn}
                />,
              ]}
            />
            <div className={cn("footer-text-two")}>
              <Trans
                i18nKey={
                  "core-flights-cfar.howItWorksDialog.footer.cfarAdditionalDescription"
                }
                components={[
                  <Modal
                    className={clsx(cn("terms-conditions-link"), cn("link"))}
                    link={termsAndConditionsLink}
                    cn={cn}
                  />,
                ]}
              />
            </div>
          </span>
        </div>
      </div>
    </div>
  );
};

interface NumBulletProps extends PropsWithChildren {
  num: number;
  className: string;
  bulletClassName: string;
  textClassName: string;
}

const NumBullet = ({
  num,
  className,
  bulletClassName,
  textClassName,
  children,
}: NumBulletProps) => (
  <div className={className}>
    <span className={bulletClassName}>{num}</span>
    <span className={textClassName}>{children}</span>
  </div>
);

interface BulletWithIconProps extends PropsWithChildren {
  icon: {
    iconName: IconNameEnum;
    className?: string;
    iconProps?: any;
  };
  className?: string;
  textClassName: string;
}

const BulletWithIcon = ({
  icon,
  className,
  textClassName,
  children,
}: BulletWithIconProps) => (
  <div className={className}>
    <Icon
      iconName={icon.iconName}
      className={icon.className}
      {...icon.iconProps}
    />
    <span className={textClassName}>{children}</span>
  </div>
);

// Modal with iframe
interface ModalProps extends PropsWithChildren {
  link: string;
  className?: string;
}

const Modal = ({ children, link, className }: ModalProps) => (
  <Dialog>
    <DialogTrigger>
      <span className={className}>{children}</span>
    </DialogTrigger>
    <IframeDialogContent>
      {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
      <iframe src={link}></iframe>
    </IframeDialogContent>
  </Dialog>
);
